import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import GameService from '../../services/game.service';
import models_V2Service from '../../services/models_V2.service';
import { reloadTable } from '../../actions/AsyncTable';
import { clearSelectedIds } from '../../actions/AsyncTable';
import ComponentTable from '../../components/ComponentTable';
import BackButton from '../../components/BalanceComponent/BackButton';
import usePhoneCheck from '../../hooks/usePhoneCheck';
import {
    Box,
    Paper,
    Toolbar,
    Typography,
    Stack,
    IconButton,
    Tooltip,
    FormControl,
    NativeSelect,
    InputLabel,
} from '@mui/material';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';

export default function VillageSettingsPage() {
    const [walkingTimeMinutes, setWalkingTimeMinutes] = useState('');
    const [taskQuantity, setTaskQuantity] = useState('');
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mobile = usePhoneCheck();
    const { selectedIds, table } = useSelector(state => state.AsyncTable);
    const isEmpty = table.isEmpty;
    const walkingTimeOptions = [1, 3, 5];
    const taskQuantityOptions = [1, 3, 5, 7];

    const onSuccessClick = async () => {
        if (walkingTimeMinutes === '' && taskQuantity === '') {
            toast.error(t('gameSettings.error'));
            return;
        }

        const requestBody = {
            users: selectedIds.map(user => {
                const userData = { user_id: user.id };

                if (walkingTimeMinutes !== '') {
                    userData.wolking_time = walkingTimeMinutes * 60;
                }
                if (taskQuantity !== '') {
                    userData.quantity_tasks = Number(taskQuantity);
                }
                return userData;
            }),
        };

        try {
            await GameService.ChangeStudentVillageSettings(requestBody);
            dispatch(reloadTable());
            setWalkingTimeMinutes('');
            setTaskQuantity('');
            dispatch(clearSelectedIds());
        } catch (error) {
            console.error(error);
        }
    };

    const onCloseClick = () => {
        dispatch(clearSelectedIds());
    };

    return (
        <Box sx={{ width: '100%' }}>
            <BackButton />
            <Paper sx={{ width: '100%', mb: 2, p: 3 }}>
                {selectedIds?.length ? (
                    <Toolbar
                        sx={{
                            mt: 2,
                            mb: 2,
                            pl: { sm: 2 },
                            pr: { xs: 1, sm: 1 },
                            bgcolor: '#F4F6F8',
                        }}
                    >
                        <Stack spacing={3} sx={{ width: '100%' }}>
                            <Stack
                                direction={mobile ? 'column' : 'row'}
                                justifyContent="flex-start"
                                spacing={2}
                                style={{ marginTop: '8px', padding: '14px' }}
                            >
                                <Stack
                                    direction={mobile ? 'column' : 'row'}
                                    spacing={3}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel shrink htmlFor="walkingTime-select">
                                            {t('table.wolking_time')}
                                        </InputLabel>
                                        <NativeSelect
                                            value={walkingTimeMinutes}
                                            onChange={e => setWalkingTimeMinutes(Number(e.target.value))}
                                            inputProps={{
                                                name: 'walkingTime',
                                                id: 'walkingTime-select',
                                            }}
                                        >
                                            <option value="" disabled>
                                                {t('gameSettings.selectTime')}
                                            </option>
                                            {walkingTimeOptions.map(option => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </NativeSelect>
                                    </FormControl>

                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <InputLabel shrink htmlFor="taskQuantity-select">
                                            {t('table.quantity_tasks')}
                                        </InputLabel>
                                        <NativeSelect
                                            value={taskQuantity}
                                            onChange={e => setTaskQuantity(Number(e.target.value))}
                                            inputProps={{
                                                name: 'taskQuantity',
                                                id: 'taskQuantity-select',
                                            }}
                                        >
                                            <option value="" disabled>
                                                {t('gameSettings.selectQuantity')}
                                            </option>
                                            {taskQuantityOptions.map(option => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </NativeSelect>
                                    </FormControl>
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={2}
                                    width={mobile ? '60%' : '100%'}
                                    sx={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Typography
                                        sx={{ flex: '1 1 100%' }}
                                        color="inherit"
                                        variant="subtitle1"
                                        component="div"
                                    >
                                        <div
                                            style={{
                                                display: 'inline-block',
                                                maxWidth: 250,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                padding: '6px 14px',
                                                borderRadius: '8px',
                                                backgroundColor: '#f0fdf4',
                                                border: '1px solid rgba(0, 171, 85, 0.2)',
                                                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.2)',
                                                cursor: 'default',
                                                fontSize: '1rem',
                                                fontWeight: 500,
                                                lineHeight: 1.75,
                                                transition: 'all 0.2s ease-in-out',
                                            }}
                                            title={
                                                selectedIds
                                                    ?.map(user => `${user.first_name} ${user.last_name}`)
                                                    .join('\n') || ''
                                            }
                                        >
                                            {`${t('gameSettings.selectedStudents')} ${selectedIds?.length}`}
                                        </div>
                                    </Typography>

                                    <Tooltip title={t('gameSettings.saveSettings')}>
                                        <IconButton onClick={onSuccessClick}>
                                            <DoneOutlineIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title={t('gameSettings.close')}>
                                        <IconButton onClick={onCloseClick}>
                                            <CloseIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Toolbar>
                ) : null}

                <ComponentTable
                    source={{
                        getData: (page, size, sort, filter) =>
                            GameService.GetStudentVillageSettings(false, {
                                page: page || 0,
                                size: size || 10,
                                orderBy: (sort && sort.orderBy) || null,
                                order: (sort && sort.order) || 'asc',
                                filter,
                            }),
                        getModel: () => models_V2Service.GetVillageSettings(),
                    }}
                    actions={[]}
                    selectable={true}
                    enableFilter
                    title={t('modals.list')}
                    enableSettings
                    toolbarvisible={true}
                    typeTable="VillageSettings"
                />
            </Paper>
        </Box>
    );
}
